import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/client';

const useExpiryWarning = () => {
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [expiryData, setExpiryData] = useState({
    expiryDate: null,
    contactEmail: '',
    contactPhone: '',
    daysRemaining: null
  });
  
  const fetchingRef = useRef(false);
  const hasCheckedRef = useRef(false);

  // Check if user dismissed warning in current session
  const checkSessionDismissal = () => {
    return sessionStorage.getItem('expiryWarningDismissed') === 'true';
  };

  // Mark warning as dismissed for current session
  const dismissWarning = () => {
    sessionStorage.setItem('expiryWarningDismissed', 'true');
    setShowExpiryWarning(false);
  };

  // Calculate days remaining until expiry
  const calculateDaysRemaining = (expiryDateString) => {
    if (!expiryDateString) return null;
    
    const now = new Date();
    const expiryDate = new Date(expiryDateString);
    
    // Calculate the difference in milliseconds
    const timeDiff = expiryDate.getTime() - now.getTime();
    
    // Convert to days (including partial days)
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  };

  // Check if warning should be shown
  const shouldShowWarning = (daysRemaining) => {
    // PRODUCTION: Change back to daysRemaining <= 3 for 3-day warning
    return daysRemaining !== null && daysRemaining <= 3;
  };

  // Fetch expiry information with proper error handling and loop prevention
  const fetchExpiryInfo = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      return;
    }

    // Only check once per session unless explicitly called
    if (hasCheckedRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      hasCheckedRef.current = true;

      const userRole = localStorage.getItem('role');
      let expiryDate = null;
      let contactInfo = {
        email: process.env.REACT_APP_SUPPORT_EMAIL ,
        phone: process.env.REACT_APP_SUPPORT_PHONE 
      };


      if (userRole === 'Candidate') {
        // For candidates, get company contact info and active next expiry
        try {
          const userRes = await api.get('observability/user-credits/');
          const userData = userRes.data.results || userRes.data;
          expiryDate = userData.next_expiry || null;
          if (userData.company_email) contactInfo.email = userData.company_email;
          if (userData.company_phone) contactInfo.phone = userData.company_phone;
        } catch (userErr) {
          console.log('Could not fetch user contact info:', userErr);
        }
      } else {
        // For admin roles, get active next expiry from credits/balance
        try {
          const res = await api.get('credits/balance/');
          expiryDate = res.data?.next_expiry || null;
        } catch (balErr) {
          console.log('Could not fetch credit balance:', balErr);
        }
      }

      if (expiryDate) {
        const daysRemaining = calculateDaysRemaining(expiryDate);

        setExpiryData({
          expiryDate,
          contactEmail: contactInfo.email,
          contactPhone: contactInfo.phone,
          daysRemaining
        });

        // Show warning if conditions are met and not already dismissed
        if (shouldShowWarning(daysRemaining) && !checkSessionDismissal()) {
          setShowExpiryWarning(true);
        } 
      } else {
        console.log('No expiry date found');
      }
    } catch (error) {
      console.error('Error fetching expiry information:', error);
      
      // Don't try to fallback to localStorage on API errors to prevent loops
      // Just log the error and continue
    } finally {
      fetchingRef.current = false;
    }
  }, []); 

  // Initialize expiry check on mount - but only once
  useEffect(() => {
    // Only check on dashboard pages or after login
    const currentPath = window.location.pathname;
    const isDashboardOrLogin = currentPath.includes('dashboard') || 
                              currentPath.includes('details') || 
                              currentPath === '/' ||
                              currentPath === '/details'; 
    
    // console.log('Path check:', { currentPath, isDashboardOrLogin, hasChecked: hasCheckedRef.current });
    
    if (isDashboardOrLogin && !hasCheckedRef.current) {
      // console.log('Initializing expiry check for path:', currentPath);
      const timer = setTimeout(() => {
        fetchExpiryInfo();
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      console.log('Skipping expiry check - wrong path or already checked');
    }
  }, [fetchExpiryInfo]); 

  // Listen for login events to trigger expiry check
  useEffect(() => {
    const handleLoginSuccess = () => {
      console.log('Login event detected, resetting expiry check');
      
      // Clear any previous session dismissal on new login
      sessionStorage.removeItem('expiryWarningDismissed');
      
      // Reset refs to allow new check
      hasCheckedRef.current = false;
      fetchingRef.current = false;
      
      // Check expiry after a brief delay
      setTimeout(() => {
        fetchExpiryInfo();
      }, 2000);
    };

    // Listen for custom login event
    window.addEventListener('userLoggedIn', handleLoginSuccess);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleLoginSuccess);
    };
  }, [fetchExpiryInfo]);

  // Manual refetch function (for testing or manual refresh)
  const refetchExpiryInfo = useCallback(() => {
    // console.log('Manual refetch triggered');
    hasCheckedRef.current = false;
    fetchingRef.current = false;
    fetchExpiryInfo();
  }, [fetchExpiryInfo]);

  return {
    showExpiryWarning,
    expiryData,
    dismissWarning,
    refetchExpiryInfo
  };
};

export default useExpiryWarning;